/**
 * Analytics filters component for advanced data filtering
 * Provides filtering capabilities for consultant, category, status, and custom date ranges
 */
import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Chip,
    Button,
    IconButton,
    Collapse,
    Grid,
    Autocomplete
} from '@mui/material';
import {
    FilterList,
    ExpandMore,
    ExpandLess,
    Clear,
    Search
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers/DatePicker';

// Interfaces
export interface ScoreRange {
    min: number | null;
    max: number | null;
}

export interface CustomDateRange {
    start: Date | null;
    end: Date | null;
}

export interface AnalyticsFiltersState {
    consultants: any[];
    categories: string[];
    statuses: string[];
    companies: any[];
    scoreRange: ScoreRange;
    customDateRange: CustomDateRange;
    companySearch?: string;
}

interface AnalyticsFiltersProps {
    filters: AnalyticsFiltersState;
    onFiltersChange: (filters: AnalyticsFiltersState) => void;
    availableConsultants?: any[];
    availableCategories?: string[];
    loading?: boolean;
}

const AnalyticsFilters: React.FC<AnalyticsFiltersProps> = ({
    filters,
    onFiltersChange,
    availableConsultants = [],
    availableCategories = ['Presentation Structure', 'Soft Skills', 'Solution Knowledge'],
    loading = false
}) => {
    const [expanded, setExpanded] = useState(false);
    const [localFilters, setLocalFilters] = useState<AnalyticsFiltersState>(filters);

    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    const handleFilterChange = (key: keyof AnalyticsFiltersState, value: any) => {
        const newFilters = { ...localFilters, [key]: value };
        setLocalFilters(newFilters);
    };

    const handleApplyFilters = () => {
        onFiltersChange(localFilters);
    };

    const handleClearFilters = () => {
        const clearedFilters: AnalyticsFiltersState = {
            consultants: [],
            categories: [],
            statuses: [],
            companies: [],
            scoreRange: { min: null, max: null },
            customDateRange: { start: null, end: null },
            companySearch: ''
        };
        setLocalFilters(clearedFilters);
        onFiltersChange(clearedFilters);
    };

    const getActiveFilterCount = () => {
        let count = 0;
        if (localFilters.consultants?.length > 0) count++;
        if (localFilters.categories?.length > 0) count++;
        if (localFilters.statuses?.length > 0) count++;
        if (localFilters.companies?.length > 0) count++;
        if (localFilters.companySearch) count++;
        if (localFilters.scoreRange?.min !== null || localFilters.scoreRange?.max !== null) count++;
        if (localFilters.customDateRange?.start || localFilters.customDateRange?.end) count++;
        return count;
    };

    const statusOptions = [
        { value: 'NEW', label: 'New' },
        { value: 'IN_PROCESS', label: 'In Process' },
        { value: 'COMPLETE', label: 'Complete' },
        { value: 'FAILED', label: 'Failed' }
    ];

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FilterList />
                            <Typography variant="h6">
                                Advanced Filters
                            </Typography>
                            {getActiveFilterCount() > 0 && (
                                <Chip
                                    label={`${getActiveFilterCount()} active`}
                                    size="small"
                                    color="primary"
                                />
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {getActiveFilterCount() > 0 && (
                                <Button
                                    size="small"
                                    startIcon={<Clear />}
                                    onClick={handleClearFilters}
                                >
                                    Clear All
                                </Button>
                            )}
                            <IconButton
                                onClick={() => setExpanded(!expanded)}
                                disabled={loading}
                            >
                                {expanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </Box>
                    </Box>

                    <Collapse in={expanded}>
                        <Box sx={{ mt: 3 }}>
                            <Grid container spacing={3}>
                                {/* Consultant Filter */}
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Autocomplete
                                        multiple
                                        options={availableConsultants}
                                        getOptionLabel={(option) => option.name || `${option.firstName} ${option.lastName}`.trim()}
                                        value={localFilters.consultants || []}
                                        onChange={(event, newValue) => handleFilterChange('consultants', newValue)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Filter by Consultants"
                                                placeholder="Select consultants"
                                            />
                                        )}
                                        renderTags={(value: any[], getTagProps) =>
                                            value.map((option, index) => {
                                                const { key, ...tagProps } = getTagProps({ index });
                                                return (
                                                    <Chip
                                                        key={key}
                                                        variant="outlined"
                                                        label={option.name || `${option.firstName} ${option.lastName}`.trim()}
                                                        {...tagProps}
                                                    />
                                                );
                                            })
                                        }
                                    />
                                </Grid>

                                {/* Category Filter */}
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <FormControl fullWidth>
                                        <InputLabel>Filter by Categories</InputLabel>
                                        <Select
                                            multiple
                                            value={localFilters.categories || []}
                                            onChange={(e) => handleFilterChange('categories', e.target.value)}
                                            label="Filter by Categories"
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {(selected as string[]).map((value) => (
                                                        <Chip key={value} label={value} size="small" />
                                                    ))}
                                                </Box>
                                            )}
                                        >
                                            {availableCategories.map((category) => (
                                                <MenuItem key={category} value={category}>
                                                    {category}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Status Filter */}
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <FormControl fullWidth>
                                        <InputLabel>Filter by Status</InputLabel>
                                        <Select
                                            multiple
                                            value={localFilters.statuses || []}
                                            onChange={(e) => handleFilterChange('statuses', e.target.value)}
                                            label="Filter by Status"
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {(selected as string[]).map((value) => {
                                                        const status = statusOptions.find(s => s.value === value);
                                                        return (
                                                            <Chip key={value} label={status?.label || value} size="small" />
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                        >
                                            {statusOptions.map((status) => (
                                                <MenuItem key={status.value} value={status.value}>
                                                    {status.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Company Filter */}
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Filter by Company"
                                        value={localFilters.companySearch || ''}
                                        onChange={(e) => handleFilterChange('companySearch', e.target.value)}
                                        placeholder="Enter company name"
                                    />
                                </Grid>

                                {/* Score Range Filter */}
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <TextField
                                            label="Min Score"
                                            type="number"
                                            inputProps={{ min: 0, max: 5, step: 0.1 }}
                                            value={localFilters.scoreRange?.min ?? ''}
                                            onChange={(e) => handleFilterChange('scoreRange', {
                                                ...localFilters.scoreRange,
                                                min: e.target.value ? parseFloat(e.target.value) : null
                                            })}
                                            sx={{ flex: 1 }}
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                            to
                                        </Typography>
                                        <TextField
                                            label="Max Score"
                                            type="number"
                                            inputProps={{ min: 0, max: 5, step: 0.1 }}
                                            value={localFilters.scoreRange?.max ?? ''}
                                            onChange={(e) => handleFilterChange('scoreRange', {
                                                ...localFilters.scoreRange,
                                                max: e.target.value ? parseFloat(e.target.value) : null
                                            })}
                                            sx={{ flex: 1 }}
                                        />
                                    </Box>
                                </Grid>

                                {/* Custom Date Range */}
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <MUIDatePicker
                                            label="Start Date"
                                            value={localFilters.customDateRange?.start || null}
                                            onChange={(newValue: Date | null) => handleFilterChange('customDateRange', {
                                                ...localFilters.customDateRange,
                                                start: newValue
                                            })}
                                            slots={{ textField: (params: any) => <TextField {...params} sx={{ flex: 1 }} /> }}
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                            to
                                        </Typography>
                                        <MUIDatePicker
                                            label="End Date"
                                            value={localFilters.customDateRange?.end || null}
                                            onChange={(newValue: Date | null) => handleFilterChange('customDateRange', {
                                                ...localFilters.customDateRange,
                                                end: newValue
                                            })}
                                            slots={{ textField: (params: any) => <TextField {...params} sx={{ flex: 1 }} /> }}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* Apply Filters Button */}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleClearFilters}
                                    disabled={loading || getActiveFilterCount() === 0}
                                >
                                    Clear Filters
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<Search />}
                                    onClick={handleApplyFilters}
                                    disabled={loading}
                                >
                                    Apply Filters
                                </Button>
                            </Box>
                        </Box>
                    </Collapse>
                </CardContent>
            </Card>
        </LocalizationProvider>
    );
};

export default AnalyticsFilters;
